///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Ocean slab under the storm column's sea-surface plane: four faces
/// colored by sea surface temperature (storm_column_sst.glsl) so the fuel
/// the storm runs on shows at the cut edges of the block.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec2, vec4 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type StormSstField } from "./StormSstField"
import { boxHeightKm, type StormColumnLayout } from "./stormColumnLayout"

/** Slab thickness as a fraction of the box height */
const cSlabFraction = 0.12

export class StormSstSlabNode extends NodeBase {
	private readonly slabRenderer: StormSstSlabRenderer
	public readonly slabKm: number

	constructor(context: StovokorContext, layout: StormColumnLayout, field: StormSstField) {
		super()
		this.slabKm = boxHeightKm(layout) * cSlabFraction
		const geometry = new StormSstSlabGeometry(context, layout, this.slabKm)
		this.slabRenderer = new StormSstSlabRenderer(context, geometry)
		this.slabRenderer.center.value = new vec2(layout.centerLat, layout.centerLon)
		this.slabRenderer.strike.value = layout.strike
		this.slabRenderer.slabKm.value = this.slabKm
		this.slabRenderer.sstTexture.value = field.getTexture(context)
		this.slabRenderer.sstBounds.value = new vec4(field.bounds.minLon, field.bounds.minLat, field.bounds.maxLon, field.bounds.maxLat)
		this.slabRenderer.sstRange.value = new vec2(field.vmin, field.vmax)
		this.renderer = this.slabRenderer
		this.renderOrder = 1
	}

	public set opacity(value: number) {
		this.slabRenderer.opacity.value = value
	}
}

class StormSstSlabRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public center = new UniformDynamic<vec2>(new vec2(0, 0))
	public strike = new UniformDynamic<number>(0)
	public sstBounds = new UniformDynamic<vec4>(new vec4(0, 0, 1, 1))
	public sstRange = new UniformDynamic<vec2>(new vec2(-2, 35))
	public sstTexture = new UniformDynamic<Texture>()
	public slabKm = new UniformDynamic<number>(1)
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("storm_column_sst"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class StormSstSlabGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, layout: StormColumnLayout, slabKm: number) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		const hx = layout.lengthKm / 2
		const hz = layout.widthKm / 2
		const bottom = -slabKm
		// Faces are subdivided along their length so the lat/lon mapping stays accurate
		const segments = 24
		const face = (a: [number, number], b: [number, number]): void => {
			for (let i = 0; i < segments; i++) {
				const t0 = i / segments
				const t1 = (i + 1) / segments
				const x0 = a[0] + (b[0] - a[0]) * t0, z0 = a[1] + (b[1] - a[1]) * t0
				const x1 = a[0] + (b[0] - a[0]) * t1, z1 = a[1] + (b[1] - a[1]) * t1
				const base = this.position.count
				this.position.addPos(x0, 0, z0)
				this.position.addPos(x1, 0, z1)
				this.position.addPos(x1, bottom, z1)
				this.position.addPos(x0, bottom, z0)
				this.indexBuffer.quad(base, base + 1, base + 2, base + 3)
			}
		}
		face([-hx, -hz], [hx, -hz])
		face([hx, -hz], [hx, hz])
		face([hx, hz], [-hx, hz])
		face([-hx, hz], [-hx, -hz])
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
